import type { ReactElement } from 'react';
import type { IconType } from 'react-icons';
import {
  LuListChecks,
  LuTarget,
  LuUserCheck,
  LuUsers,
} from 'react-icons/lu';

import SbrIcon from './SbrIcon';

const TEAL = '#00c2ab';
const BLUE = '#2b93f0';
const NAVY = '#0a0f1f';
const BLUE_LIGHT = '#5eb3f6';
const TEAL_MINT = '#2dd4bf';
const INDIGO = '#6366f1';

type Metric = {
  label: string;
  value: string;
  Icon: IconType;
  tone: 'teal' | 'blue';
};

const metrics: Metric[] = [
  { label: 'Target Roles', value: '18', Icon: LuTarget, tone: 'teal' },
  { label: 'Engaged Talent', value: '245', Icon: LuUsers, tone: 'teal' },
  { label: 'Interview Ready', value: '48', Icon: LuUserCheck, tone: 'blue' },
  { label: 'Shortlist', value: '12', Icon: LuListChecks, tone: 'blue' },
];

const topFunctions: { label: string; pct: number; tone: 'teal' | 'sky' | 'blue' }[] = [
  { label: 'Technology', pct: 32, tone: 'teal' },
  { label: 'Sales & Revenue', pct: 24, tone: 'teal' },
  { label: 'Operations', pct: 18, tone: 'sky' },
  { label: 'Finance', pct: 14, tone: 'blue' },
  { label: 'People Ops', pct: 12, tone: 'blue' },
];

/** One ring segment per function — each a distinct color */
const DONUT_SEGMENT_COLORS: Record<(typeof topFunctions)[number]['label'], string> = {
  Technology: TEAL,
  'Sales & Revenue': TEAL_MINT,
  Operations: BLUE_LIGHT,
  Finance: BLUE,
  'People Ops': INDIGO,
};

const donutSegments = topFunctions.map((f) => ({
  label: f.label,
  length: f.pct,
  color: DONUT_SEGMENT_COLORS[f.label],
}));

const DONUT_R = 52;
const DONUT_C = 2 * Math.PI * DONUT_R;

/** Peaks and dips in the middle, finishes on a high */
const HEALTH_VALUES = [
  34, 38, 35, 42, 36, 45, 40, 48, 43, 51, 46, 54, 49, 44, 39, 43, 46, 50, 54, 58,
];

const HEALTH_DOT_SIZE = 2.5;

const CHART_W = 280;
const CHART_H = 72;
const CHART_PAD = { t: 8, r: 6, b: 8, l: 6 };
const GRID_COLS = 7;
const GRID_ROWS = 4;

function healthChartCoords() {
  const min = Math.min(...HEALTH_VALUES);
  const max = Math.max(...HEALTH_VALUES);
  const innerW = CHART_W - CHART_PAD.l - CHART_PAD.r;
  const innerH = CHART_H - CHART_PAD.t - CHART_PAD.b;

  return HEALTH_VALUES.map((v, i) => {
    const x = CHART_PAD.l + (i / (HEALTH_VALUES.length - 1)) * innerW;
    const y = CHART_PAD.t + (1 - (v - min) / (max - min || 1)) * innerH;
    return { x, y };
  });
}

/** Smooth cubic curve through points */
function smoothLinePath(points: { x: number; y: number }[]) {
  if (points.length < 2) return '';
  let d = `M ${points[0].x} ${points[0].y}`;
  for (let i = 0; i < points.length - 1; i++) {
    const p0 = points[i - 1] ?? points[i];
    const p1 = points[i];
    const p2 = points[i + 1];
    const p3 = points[i + 2] ?? p2;
    const cp1x = p1.x + (p2.x - p0.x) / 6;
    const cp1y = p1.y + (p2.y - p0.y) / 6;
    const cp2x = p2.x - (p3.x - p1.x) / 6;
    const cp2y = p2.y - (p3.y - p1.y) / 6;
    d += ` C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${p2.x} ${p2.y}`;
  }
  return d;
}

function healthDiamondPoints(x: number, y: number, size = HEALTH_DOT_SIZE) {
  return `${x},${y - size} ${x + size},${y} ${x},${y + size} ${x - size},${y}`;
}

function ChartGrid() {
  const innerW = CHART_W - CHART_PAD.l - CHART_PAD.r;
  const innerH = CHART_H - CHART_PAD.t - CHART_PAD.b;
  const x0 = CHART_PAD.l;
  const y0 = CHART_PAD.t;
  const x1 = CHART_W - CHART_PAD.r;
  const y1 = CHART_H - CHART_PAD.b;
  const lines: ReactElement[] = [];

  for (let r = 0; r <= GRID_ROWS; r++) {
    const y = y0 + (r / GRID_ROWS) * innerH;
    lines.push(
      <line
        key={`h-${r}`}
        x1={x0}
        y1={y}
        x2={x1}
        y2={y}
        stroke="rgba(255,255,255,0.07)"
        strokeWidth="1"
      />,
    );
  }
  for (let c = 0; c <= GRID_COLS; c++) {
    const x = x0 + (c / GRID_COLS) * innerW;
    lines.push(
      <line
        key={`v-${c}`}
        x1={x}
        y1={y0}
        x2={x}
        y2={y1}
        stroke="rgba(255,255,255,0.07)"
        strokeWidth="1"
      />,
    );
  }

  return <g className="pl-dashboard__grid">{lines}</g>;
}

function DonutChart() {
  let offset = 0;
  return (
    <div className="pl-dashboard__donut" aria-hidden>
      <svg viewBox="0 0 140 140" className="pl-dashboard__donut-svg">
        <circle
          cx="70"
          cy="70"
          r={DONUT_R}
          fill="none"
          stroke="rgba(255,255,255,0.08)"
          strokeWidth="14"
        />
        {donutSegments.map((seg) => {
          const dash = (seg.length / 100) * DONUT_C;
          const gap = DONUT_C - dash;
          const circle = (
            <circle
              key={seg.label}
              cx="70"
              cy="70"
              r={DONUT_R}
              fill="none"
              stroke={seg.color}
              strokeWidth="14"
              strokeDasharray={`${dash} ${gap}`}
              strokeDashoffset={-offset}
              transform="rotate(-90 70 70)"
              strokeLinecap="butt"
            />
          );
          offset += dash;
          return circle;
        })}
      </svg>
      <div className="pl-dashboard__donut-center">
        <div className="pl-dashboard__donut-value">245</div>
        <div className="pl-dashboard__donut-label">Active Talent</div>
      </div>
    </div>
  );
}

function HealthSparkline() {
  const coords = healthChartCoords();
  const linePath = smoothLinePath(coords);
  const bottom = CHART_H - CHART_PAD.b;
  const areaPath = `${linePath} L ${coords[coords.length - 1].x} ${bottom} L ${coords[0].x} ${bottom} Z`;
  return (
    <svg
      viewBox={`0 0 ${CHART_W} ${CHART_H}`}
      className="pl-dashboard__sparkline"
      preserveAspectRatio="none"
      aria-hidden
    >
      <defs>
        <linearGradient id="pl-health-fill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={TEAL} stopOpacity="0.38" />
          <stop offset="85%" stopColor={TEAL} stopOpacity="0.06" />
          <stop offset="100%" stopColor={TEAL} stopOpacity="0" />
        </linearGradient>
      </defs>
      <ChartGrid />
      <path d={areaPath} fill="url(#pl-health-fill)" />
      <path
        d={linePath}
        fill="none"
        stroke={TEAL}
        strokeWidth="1.25"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      {coords.map((p, i) => (
        <polygon
          key={i}
          points={healthDiamondPoints(p.x, p.y)}
          fill={TEAL}
          stroke={NAVY}
          strokeWidth="0.75"
        />
      ))}
    </svg>
  );
}

export default function PipelineTalentDashboard() {
  return (
    <div className="pl-dashboard">
      <div className="pl-dashboard__title">Your Talent Pipeline</div>

      <div className="pl-dashboard__body">
        <div className="pl-dashboard__metrics">
          {metrics.map((m) => (
            <div key={m.label} className={`pl-dashboard__metric pl-dashboard__metric--${m.tone}`}>
              <div className="pl-dashboard__metric-icon">
                <SbrIcon icon={m.Icon} size={18} />
              </div>
              <div className="pl-dashboard__metric-main">
                <span className="pl-dashboard__metric-label">{m.label}</span>
                <span className="pl-dashboard__metric-value">{m.value}</span>
              </div>
            </div>
          ))}
        </div>

        <DonutChart />

        <div className="pl-dashboard__functions">
          <div className="pl-dashboard__functions-title">Top Functions</div>
          {topFunctions.map((f) => (
            <div key={f.label} className="pl-dashboard__bar-row">
              <div className="pl-dashboard__bar-head">
                <span className="pl-dashboard__bar-label">{f.label}</span>
                <span className="pl-dashboard__bar-pct">{f.pct}%</span>
              </div>
              <div className="pl-dashboard__bar-track">
                <div
                  className={`pl-dashboard__bar-fill pl-dashboard__bar-fill--${f.tone}`}
                  style={{ width: `${f.pct}%` }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="pl-dashboard__health">
        <div className="pl-dashboard__health-chart">
          <div className="pl-dashboard__health-title">Pipeline Health</div>
          <div className="pl-dashboard__sparkline-wrap">
            <HealthSparkline />
          </div>
        </div>
        <div className="pl-dashboard__health-summary">
          <div className="pl-dashboard__health-status">Strong</div>
          <div className="pl-dashboard__health-delta">+18% vs last month</div>
        </div>
      </div>
    </div>
  );
}
